import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import { TextStyle } from "@tiptap/extension-text-style";
import { Extension } from "@tiptap/core";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { useEffect, useState } from "react";
import { Button } from "reactstrap";

import { httpRequest } from "utils/httpRequest";

// Custom Font Size Extension
const FontSize = Extension.create({
  name: "fontSize",

  addOptions() {
    return {
      types: ["textStyle"],
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) =>
              element.style.fontSize?.replace(/['"]+/g, "") || null,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) {
                return {};
              }
              return {
                style: `font-size: ${attributes.fontSize}`,
              };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      setFontSize:
        (fontSize) =>
          ({ commands }) => {
            return commands.setMark("textStyle", { fontSize });
          },
      unsetFontSize:
        () =>
          ({ commands }) => {
            return commands.setMark("textStyle", { fontSize: null });
          },
    };
  },
});

const fontSizes = [
  // { label: "6", value: "6px" },
  // { label: "8", value: "8px" },
  { label: "10", value: "10px" },
  { label: "12", value: "12px" },
  { label: "14", value: "14px" },
  { label: "16", value: "16px" },
  { label: "18", value: "18px" },
  { label: "20", value: "20px" },
  { label: "22", value: "22px" },
  { label: "24", value: "24px" },
  { label: "26", value: "26px" },
  { label: "28", value: "28px" },
  { label: "30", value: "30px" },
  { label: "32", value: "32px" },
];

const TextEditor = ({
  value = "",
  onChange,
  onBlur,
  placeholder = "Write...",
  hideImageAndLink,
}) => {
  const [sizeDropdownOpen, setSizeDropdownOpen] = useState(false);
  const toggleSizeDropdown = () => setSizeDropdownOpen((prev) => !prev);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle, // Required for font size to work
      FontSize, // Our custom font size extension
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "editor-link",
          rel: "noopener noreferrer",
          target: "_blank",
        },
        validate: (href) => /^https?:\/\//.test(href),
      }),
      Highlight.configure({
        multicolor: true,
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          class: "embedded-image",
          style: "max-width: 100%; height: auto;",
        },
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
    onBlur: onBlur,
    editorProps: {
      attributes: {
        class: "tiptap-content",
        placeholder: placeholder,
      },
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false); // false = don't trigger onChange
    }
  }, [value, editor]);

  if (!editor) return null;

  const addImage = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append("file", file);

      try {
        // Upload to backend
        const response = await httpRequest(
          "admin/UploadMedia",
          "POST",
          formData,
          {},
          true,
          true
        );

        const data = response?.filePath;

        const imageUrl = `${process.env.REACT_APP_IMAGE_URL}${data}`;
        editor
          .chain()
          .focus()
          .setImage({ src: imageUrl })
          .insertContent("<p></p>")
          .run();
      } catch (err) {
        console.error("Image upload failed:", err);
      }
    };

    input.click();
  };

  const setLink = () => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", previousUrl || "https://");

    if (url === null) {
      return;
    }

    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    // Validate URL format
    if (!url.match(/^https?:\/\//)) {
      alert("Please enter a valid URL starting with http:// or https://");
      return;
    }

    // Check if there's selected text
    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to);

    if (selectedText) {
      // If text is selected, apply link to selection
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: url })
        .run();
    } else {
      // If no text is selected, insert the URL as both text and link
      editor.chain().focus().insertContent(`<a href="${url}">${url}</a>`).run();
    }
  };

  // Get current font size for display
  const getCurrentFontSize = () => {
    const attributes = editor.getAttributes("textStyle");
    return attributes.fontSize || "16px"; // Default font size
  };

  const currentFontSize = getCurrentFontSize();
  const currentFontSizeLabel =
    fontSizes.find((size) => size.value === currentFontSize)?.label || "16";

  return (
    <>
      <div className="mb-2 d-flex flex-wrap py-4" style={{ gap: "10px 0px" }}>
        {/* Text formatting */}
        <Button
          size="sm"
          color={editor.isActive("bold") ? "primary" : "secondary"}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <i className="fas fa-bold" />
        </Button>

        <Button
          size="sm"
          color={editor.isActive("italic") ? "primary" : "secondary"}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <i className="fas fa-italic" />
        </Button>

        <Button
          size="sm"
          color={editor.isActive("underline") ? "primary" : "secondary"}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <i className="fas fa-underline" />
        </Button>

        <Button
          size="sm"
          color={editor.isActive("highlight") ? "primary" : "secondary"}
          onClick={() => editor.chain().focus().toggleHighlight().run()}
        >
          <i className="fas fa-highlighter" />
        </Button>

        {/* Double Quotes */}
        <Button
          size="sm"
          onClick={() => {
            const { from, to } = editor.state.selection;
            const selectedText = editor.state.doc.textBetween(from, to);

            if (selectedText) {
              // If text is selected, wrap it in quotes
              editor
                .chain()
                .focus()
                .command(({ tr }) => {
                  tr.insertText('"', from);
                  tr.insertText('"', to + 1);
                  return true;
                })
                .run();
            } else {
              // If no text selected, insert empty quotes and position cursor between them
              editor.chain().focus().insertContent('""').run();
              // Position cursor between quotes
              setTimeout(() => {
                const pos = editor.state.selection.from - 1;
                editor.commands.setTextSelection(pos);
              }, 10);
            }
          }}
          title="Add quotes"
        >
          "
        </Button>

        {/* Lists */}
        <Button
          size="sm"
          color={editor.isActive("bulletList") ? "primary" : "secondary"}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <i className="fas fa-list-ul" />
        </Button>

        <Button
          size="sm"
          color={editor.isActive("orderedList") ? "primary" : "secondary"}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <i className="fas fa-list-ol" />
        </Button>

        {/* Headings */}
        <Button
          size="sm"
          color={
            editor.isActive("heading", { level: 1 }) ? "primary" : "secondary"
          }
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 1 }).run()
          }
        >
          H1
        </Button>

        <Button
          size="sm"
          color={
            editor.isActive("heading", { level: 2 }) ? "primary" : "secondary"
          }
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 2 }).run()
          }
        >
          H2
        </Button>

        <Button
          size="sm"
          color={
            editor.isActive("heading", { level: 3 }) ? "primary" : "secondary"
          }
          onClick={() =>
            editor.chain().focus().toggleHeading({ level: 3 }).run()
          }
        >
          H3
        </Button>

        {/* Font size dropdown */}
        <Dropdown
          isOpen={sizeDropdownOpen}
          toggle={toggleSizeDropdown}
          className="fontSizeDropdown"
        >
          <DropdownToggle size="sm" color="secondary">
            {currentFontSizeLabel}
          </DropdownToggle>
          <DropdownMenu className="fontSizeDropdownMenu">
            {fontSizes.map((size) => (
              <DropdownItem
                key={size.value}
                onClick={(e) => {
                  setSizeDropdownOpen(false);
                  e.stopPropagation();
                  editor.commands.setFontSize(size.value);
                }}
                active={currentFontSize === size.value}
              >
                {size.label}
              </DropdownItem>
            ))}
            {/* <DropdownItem divider />
            <DropdownItem
              onClick={() => {
                editor.commands.unsetFontSize();
                setSizeDropdownOpen(false);
              }}
            >
              Reset to default
            </DropdownItem> */}
          </DropdownMenu>
        </Dropdown>

        {/* Blocks */}
        <Button
          size="sm"
          color={editor.isActive("blockquote") ? "primary" : "secondary"}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          <i className="fas fa-quote-right" />
        </Button>

        <Button
          size="sm"
          color={editor.isActive("codeBlock") ? "primary" : "secondary"}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        >
          <i className="fas fa-code" />
        </Button>

        {/* Insert */}
        <Button
          size="sm"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        >
          <i className="fas fa-minus" />
        </Button>

        {hideImageAndLink && (
          <Button
            size="sm"
            color={editor.isActive("link") ? "primary" : "secondary"}
            onClick={setLink}
            title="Insert/edit link"
          >
            <i className="fas fa-link" />
          </Button>
        )}

        {hideImageAndLink && (
          <Button
            size="sm"
            color={editor.isActive("image") ? "primary" : "secondary"}
            onClick={addImage}
            title="Insert image"
          >
            <i className="fas fa-image" />
          </Button>
        )}

        {/* History */}
        <Button
          size="sm"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
        >
          <i className="fas fa-undo" />
        </Button>

        <Button
          size="sm"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
        >
          <i className="fas fa-redo" />
        </Button>
      </div>

      <div
        className="border rounded p-2 bg-white"
        style={{ minHeight: "150px" }}
      >
        <EditorContent editor={editor} />
      </div>

      {/* Add CSS for better link and image display */}
      <style jsx global>{`
        .ProseMirror {
          min-height: 150px;
          padding: 5px;
        }
        .ProseMirror:focus {
          outline: none;
        }

        .ProseMirror strong,
        .ProseMirror b,
        .tiptap-content strong,
        .tiptap-content b {
          font-weight: 400 !important;
          text-shadow: 0.5px 0 0 currentColor, -0.5px 0 0 currentColor,
            0 0.5px 0 currentColor, 0 -0.5px 0 currentColor !important;
          letter-spacing: 0.02em !important;
        }

        .ProseMirror blockquote {
          border-left: 4px solid #cbd5e1;
          padding-left: 1rem;
          margin-left: 0;
          margin-right: 0;
          font-style: italic;
          color: #64748b;
          background-color: #f8fafc;
          padding: 0.5rem 1rem;
          margin: 1rem 0;
        }
        .ProseMirror a,
        .editor-link,
        .tiptap-content a {
          color: #3b82f6 !important;
          text-decoration: underline !important;
          cursor: pointer;
        }
        .ProseMirror a:hover,
        .editor-link:hover,
        .tiptap-content a:hover {
          color: #1d4ed8 !important;
        }
        .embedded-image,
        .tiptap-content img,
        .ProseMirror img {
          max-width: 100%;
          height: auto;
          display: inline-block;
        }

        /* Font size styles */
        .ProseMirror [style*="font-size"] {
          line-height: 1.4;
        }
      `}</style>
    </>
  );
};

export default TextEditor;
